import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function FloatingInquiry({ projectId, projectTitle }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [busy, setBusy] = useState(false);

    if (!projectId) return null;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/inquiries", { project_id: projectId, ...form });
            toast.success("Inquiry sent. A specialist will reach out shortly.");
            setOpen(false);
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Failed to send");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid="floating-inquiry-btn"
                className="fixed bottom-6 right-6 z-40 glass px-5 py-4 flex items-center gap-3 text-white hover:text-gold border-gold/40 hover:border-gold transition-colors"
            >
                <MessageCircle size={18} strokeWidth={1.5} />
                <span className="text-sm tracking-[0.2em] uppercase hidden sm:inline">Quick Inquiry</span>
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-6"
                        onClick={() => setOpen(false)}
                    >
                        <motion.div
                            initial={{ y: 60, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 60, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-xl bg-[#0a0a0a] border border-white/10 p-8 sm:p-10"
                            data-testid="inquiry-modal"
                        >
                            <div className="flex items-start justify-between mb-6">
                                <div>
                                    <div className="overline text-gold mb-2">Request Information</div>
                                    <h3 className="font-serif text-3xl">Speak with a specialist</h3>
                                    {projectTitle && <p className="text-white/60 text-sm mt-2">About: {projectTitle}</p>}
                                </div>
                                <button onClick={() => setOpen(false)} className="p-1 text-white/60 hover:text-gold"><X size={18} /></button>
                            </div>
                            <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
                                <input required placeholder="Your name" value={form.name} onChange={(e)=>setForm(f=>({...f,name:e.target.value}))} className="w-full bg-black border border-white/15 px-4 py-3 text-white placeholder:text-white/40 focus:border-gold focus:outline-none" data-testid="inquiry-name" />
                                <input required type="email" placeholder="Email" value={form.email} onChange={(e)=>setForm(f=>({...f,email:e.target.value}))} className="w-full bg-black border border-white/15 px-4 py-3 text-white placeholder:text-white/40 focus:border-gold focus:outline-none" data-testid="inquiry-email" />
                                <input placeholder="Phone (optional)" value={form.phone} onChange={(e)=>setForm(f=>({...f,phone:e.target.value}))} className="w-full bg-black border border-white/15 px-4 py-3 text-white placeholder:text-white/40 focus:border-gold focus:outline-none" data-testid="inquiry-phone" />
                                <textarea required placeholder="Tell us about your project…" rows={4} value={form.message} onChange={(e)=>setForm(f=>({...f,message:e.target.value}))} className="w-full bg-black border border-white/15 px-4 py-3 text-white placeholder:text-white/40 focus:border-gold focus:outline-none resize-none" data-testid="inquiry-message" />
                                <button disabled={busy} type="submit" data-testid="inquiry-submit" className="btn-gold w-full disabled:opacity-50">
                                    <Send size={16} strokeWidth={1.6} /> Send Inquiry
                                </button>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
